import { call, put, takeLatest, select } from 'redux-saga/effects';

import { captureException } from '../../../client/sentry';
import { graphql } from '../../../globals/api/requests';
import { promptToast } from '../../../components/toast-container/actions';
import { isValidEmail } from '../../account/helpers';

import genericUserReviewFragment from '../generic-user-review.graphql';

import { productReviewVoted, storeReviewVoted } from '../tracking';

import {
  CREATE_REVIEW,
  DELETE_REVIEW,
  EDIT_REVIEW,
  REPORT_REVIEW,
  LIKE_REVIEW_TOGGLE,
  closeReviewDialog,
  reviewRequestError,
  reportReviewSuccess,
  reportReviewFailed,
  likeReviewSuccess,
  likeReviewFailed,
  createReviewSuccess,
  editReviewSuccess,
  deleteReviewSuccess,
  updateReviewSummary,
} from '../actions';

import { setDisplayName } from '../../account/settings/actions/display-name';

function* updateSummaryData(id, type) {
  const response = yield graphql(
    `query updateReviewSummary($id: Int!) {
      ${type.toLowerCase()}(id: $id) {
        userReviewSummary {
          rating
          count
          countTotal
          distribution
        }
      }
    }
    `,
    { id }
  );

  if (response.errors) {
    yield put(reviewRequestError());
    yield call(captureException, response.errors);
  }

  if (
    response.data &&
    response.data.product &&
    response.data.product.userReviewSummary
  ) {
    yield put(
      updateReviewSummary({
        ...response.data.product.userReviewSummary,
        belongsToId: id,
      })
    );
  }
}

export function* createReview({
  belongsToId, // i.e. product id, store id
  rating,
  reviewType: type,
  reviewText,
  purchaseDate,
  displayName,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewCreate(
        $belongsToId: Int!,
        $type: reviewType!,
        $rating: Int!,
        $reviewText: String!,
        $purchaseDate: String
        $displayName: String
        $getProduct: Boolean = false) {
          reviewCreate(
            type: $type,
            belongsToId: $belongsToId,
            rating: $rating,
            reviewText: $reviewText,
            purchaseDate: $purchaseDate
            displayName: $displayName) {
              ...GenericUserReview
            }
      }, ${genericUserReviewFragment}`,
      {
        type,
        belongsToId,
        rating,
        reviewText,
        purchaseDate,
        displayName,
      }
    );

    if (response.errors) {
      yield put(reviewRequestError());
      yield call(captureException, response.errors);
      return;
    }

    if (displayName) {
      yield put(setDisplayName(displayName));
    }

    yield put(closeReviewDialog());
    yield put(
      createReviewSuccess({
        ...response.data.reviewCreate,
        belongsToId,
        reviewType: type,
      })
    );

    yield call(updateSummaryData, belongsToId, type);

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.create-success')
      )
    );
  } catch (e) {
    yield put(reviewRequestError());
    yield call(captureException, e);
  }
}

export function* editReview({
  reviewId,
  reviewType,
  rating,
  reviewText,
  purchaseDate,
  belongsToId,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewEdit(
        $reviewId: String!,
        $rating: Int!,
        $reviewText: String!,
        $purchaseDate: String,
        $getProduct: Boolean = false
      ) {
        reviewEdit(
          reviewId: $reviewId,
          rating: $rating,
          reviewText: $reviewText,
          purchaseDate: $purchaseDate,
        ) {
          ...GenericUserReview
        }
      }, ${genericUserReviewFragment}`,
      {
        reviewId,
        rating,
        reviewText,
        purchaseDate,
        getProduct: reviewType === 'PRODUCT',
      }
    );

    if (response.errors) {
      yield put(reviewRequestError());
      yield call(captureException, response.errors);
      return;
    }

    yield put(closeReviewDialog());
    yield put(
      editReviewSuccess({
        ...response.data.reviewEdit,
        belongsToId,
        reviewType,
      })
    );
    yield call(updateSummaryData, belongsToId, reviewType);

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.edit-success')
      )
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* deleteReview({ reviewId, reviewType, belongsToId }) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewDelete(
        $reviewId: String!,
      ) {
        reviewDelete(
          reviewId: $reviewId,
        ) {
          success
        }
      }`,
      {
        reviewId,
      }
    );

    if (response.errors) {
      yield put(reviewRequestError());
      yield call(captureException, response.errors);
      return;
    }

    yield put(closeReviewDialog());
    yield put(deleteReviewSuccess({ id: reviewId, belongsToId, reviewType }));
    yield call(updateSummaryData, belongsToId, reviewType);

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.delete-success')
      )
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* reportReview({ reviewId, message, email }) {
  if (!email || !isValidEmail(email)) {
    yield put(reportReviewFailed('error.missing.email'));
    return;
  }
  if (!message) {
    yield put(reportReviewFailed('error.missing.message'));
    return;
  }
  try {
    const response = yield call(
      graphql,
      `mutation reviewReport(
        $reviewId: String!,
        $message: String!,
        $email: String!,
      ) {
        reviewReport(
          reviewId: $reviewId,
          message: $message,
          email: $email,
        ) {
          success
        }
      }`,
      { reviewId, message, email }
    );
    if (
      response &&
      response.data &&
      response.data.reviewReport &&
      response.data.reviewReport.success
    ) {
      yield put(reportReviewSuccess());
      yield put(
        promptToast(
          'success',
          window.APP_GLOBALS.t('feedback', 'report-reviews.success')
        )
      );
    } else {
      yield put(reportReviewFailed(response ? response.errors : undefined));
    }
  } catch (err) {
    yield call(captureException, err);
    yield put(reportReviewFailed(err.message));
  }
}

function* likeReview({ id, reviewType }) {
  const query = `
    mutation($reviewId: String!) {
      reviewVoteHelpful(reviewId: $reviewId) {
        success
      }
    }
  `;

  const response = yield call(graphql, query, { reviewId: id });

  if (
    response.data.reviewVoteHelpful &&
    response.data.reviewVoteHelpful.success
  ) {
    const likedReviews = yield select(
      state => state.areas.account.me.likes.reviews
    );
    const value = likedReviews && likedReviews.includes(id) ? 'unlike' : 'like';

    if (reviewType === 'product') {
      yield call(productReviewVoted, value);
    }

    if (reviewType === 'store') {
      yield call(storeReviewVoted, value);
    }

    yield put(likeReviewSuccess(id, value));
  } else {
    yield put(likeReviewFailed());
  }
}

function* mySaga() {
  yield takeLatest(CREATE_REVIEW, createReview);
  yield takeLatest(EDIT_REVIEW, editReview);
  yield takeLatest(DELETE_REVIEW, deleteReview);
  yield takeLatest(REPORT_REVIEW, reportReview);
  yield takeLatest(LIKE_REVIEW_TOGGLE, likeReview);
}

export default mySaga;
